import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel, 
  Button,
} from "@mui/material";
import InvoicePayments from "./../../Empresa/NuevaFactura/InvoicePayments";
import InfoProduct from "../../Empresa/NuevaFactura/InvoiceProduct";
import InvoiceAccount from "../../Empresa/NuevaFactura/InvoiceAccount";
import InvoiceTotal from "../../Empresa/NuevaFactura/InvoiceTotal";

const toNum = (v) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round((toNum(n) + 1e-10) * 100) / 100;

const cleanMoney = (v) => {
  const s = String(v ?? "").replace(",", ".").trim();
  if (!s) return "0.00";
  const n = Number(s);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

export default function PurchaseDetail(props) {
  const {
    purchaseData,
    onPurchaseDataChange,
    payments,
    setPayments,
    productRows,
    setProductRows,
    accountRows,
    setAccountRows,
    descripcion: descripcionProp,
    setDescripcion: setDescripcionProp,
    productDescripcion,
    setProductDescripcion,
    onSubmit,
  } = props;

  const descripcion = descripcionProp ?? productDescripcion ?? "";
  const setDescripcion = setDescripcionProp ?? setProductDescripcion ?? (() => {});

  const [tabValue, setTabValue] = useState(0);
  const [sinMovimiento, setSinMovimiento] = useState(false);

  const [productTotals, setProductTotals] = useState(null);
  const [accountTotals, setAccountTotals] = useState(null);

  useEffect(() => {
    if (purchaseData?.sin_movimiento_inventario !== undefined) {
      setSinMovimiento(!!purchaseData.sin_movimiento_inventario);
    }
  }, [purchaseData?.sin_movimiento_inventario]);

  const handleSinMovimiento = (e) => {
    const checked = e.target.checked;
    setSinMovimiento(checked);
    onPurchaseDataChange?.((prev) => ({
      ...(prev || {}),
      sin_movimiento_inventario: checked,
    }));
  };

  const handleProductsChange = (data) => {
    if (data?.resumen_totales) setProductTotals(data.resumen_totales);
    onPurchaseDataChange?.((prev) => ({
      ...(prev || {}),
      ...(data || {}),
    }));
  };

  const handleAccountsChange = (data) => {
    if (data?.total !== undefined) {
      setAccountTotals({
        sub15: data.subtotal15 ?? 0,
        sub5: data.subtotal5 ?? 0,
        sub0: data.subtotal0 ?? 0,
        descuento: data.descuento_total ?? 0,
        iva15: data.iva15 ?? 0,
        iva5: data.iva5 ?? 0,
        ice: data.ice ?? 0,
        total: data.total ?? 0,
      });
    }
    onPurchaseDataChange?.((prev) => ({
      ...(prev || {}),
      ...(data || {}),
    }));
  };

  const merged = useMemo(() => {
    const p = productTotals || {};
    const a = accountTotals || {};

    const sub15 = round2(toNum(p.sub15) + toNum(a.sub15));
    const sub5 = round2(toNum(p.sub5) + toNum(a.sub5));
    const sub0 = round2(toNum(p.sub0) + toNum(a.sub0));
    const descuento = round2(toNum(p.descuento) + toNum(a.descuento));

    const iva15 = round2(toNum(p.iva15) + toNum(a.iva15));
    const iva5 = round2(toNum(p.iva5) + toNum(a.iva5));
    const ice = round2(toNum(p.ice) + toNum(a.ice));

    const total = round2(sub15 + sub5 + sub0 + iva15 + iva5 + ice);

    return { sub15, sub5, sub0, descuento, iva15, iva5, ice, total };
  }, [productTotals, accountTotals]);

  useEffect(() => {
    onPurchaseDataChange?.((prev) => ({
      ...(prev || {}),
      subtotal15: merged.sub15,
      subtotal5: merged.sub5,
      subtotal0: merged.sub0,
      descuento_total: merged.descuento,
      iva15: merged.iva15,
      iva5: merged.iva5,
      ice: merged.ice,
      total: merged.total,
    }));
  }, [
    merged.sub15,
    merged.sub5,
    merged.sub0,
    merged.descuento,
    merged.iva15,
    merged.iva5,
    merged.ice,
    merged.total,
    onPurchaseDataChange,
  ]);

  const formasPagoPayload = useMemo(() => {
    const arr = Array.isArray(payments) ? payments : [];
    return arr
      .map((p) => ({
        forma_pago: String(p?.forma_pago ?? "20"),
        plazo: String(p?.plazo ?? "0"),
        unidad_tiempo: String(p?.unidad_tiempo ?? "dias"),
        valor: cleanMoney(p?.valor),
      }))
      .filter((p) => Number(p.valor) > 0);
  }, [payments]);

  useEffect(() => {
    onPurchaseDataChange?.((prev) => ({
      ...(prev || {}),
      formas_pago: formasPagoPayload,
      pagos: [],
    }));
  }, [formasPagoPayload, onPurchaseDataChange]);

  const handleSubmit = () => {
    if (typeof onSubmit === "function") {
      onSubmit({
        ...(purchaseData || {}),
        sin_movimiento_inventario: sinMovimiento,
        formas_pago: formasPagoPayload,
        pagos: [],
        subtotal15: merged.sub15,
        subtotal5: merged.sub5,
        subtotal0: merged.sub0,
        descuento_total: merged.descuento,
        iva15: merged.iva15,
        iva5: merged.iva5,
        ice: merged.ice,
        total: merged.total,
      });
    }
  };

  return (
    <Box sx={{ mx: "auto", mt: 3 }}>
      <Paper
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 3,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Bodega:</Typography>

          <Select value="PRINCIPAL" size="small" disabled sx={{ width: 240 }}>
            <MenuItem value="PRINCIPAL">Bodega principal</MenuItem>
          </Select>

          <FormControlLabel
            control={<Checkbox checked={sinMovimiento} onChange={handleSinMovimiento} />}
            label="Sin movimiento de inventario"
          />

          {onSubmit ? (
            <Box sx={{ ml: "auto" }}>
              <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: "#1565C0" }}>
                Guardar
              </Button>
            </Box>
          ) : null}
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            backgroundColor: "#1565C0",
            "& .MuiTab-root": {
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              minHeight: "48px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            },
            "& .Mui-selected": {
              backgroundColor: "#fff",
              color: "#1565C0",
              borderTop: "4px solid #1565C0",
              borderLeft: "4px solid #1565C0",
              borderRight: "4px solid #1565C0",
              borderBottom: "none",
              marginTop: "5px",
              marginLeft: "10px",
            },
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          <Tab label="Productos" />
          <Tab label="Cuentas" />
          <Tab label="Formas de Pago" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <InfoProduct rows={productRows} setRows={setProductRows} onProductDataChange={handleProductsChange} />
          )}

          {tabValue === 1 && (
            <InvoiceAccount rows={accountRows} setRows={setAccountRows} onAccountDataChange={handleAccountsChange} />
          )}

          {tabValue === 2 && <InvoicePayments totalPagar={Number(merged.total || 0)} payments={payments} setPayments={setPayments} />}

          <InvoiceTotal
            descripcion={descripcion}
            setDescripcion={setDescripcion}
            productTotals={productTotals}
            accountTotals={accountTotals}
          />
        </Box>
      </Paper>
    </Box>
  );
}
